import { METADATA_FIELDS_TYPE } from "@constants";
import { Grid, List, ListItem, Typography } from "@mzc-pdc/ui";
import Chip from "@components_v2/chip";
import EllipsisTooltip from "@components_v2/ellipsis-tooltip/ellipsis-tooltip";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import AutocompleteTableList from "@features/autocomplete-table/autocomplete-table-list";
import menuService from "@services/menuService";
import { useLocation } from "react-router-dom";
import { PeopleContentColumn } from "@features/autocomplete-table/autocomplete-table-columns/autocomplete-table-columns-contents";
import LinkText from "@components_v2/link-text";

const MetadataFieldReadMode = ({ type, isHidden, value, options, variant }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);

    const getChips = useCallback(
        (type, chips) => {
            const checkOptionLabel = !!options?.find((option) => !!option.label);
            const getLabelValue = (value) => {
                return checkOptionLabel ? options?.find((option) => option.value === value)?.label : value;
            };

            return (
                <Grid container gap={0.5}>
                    {!chips?.length && "-"}
                    {chips?.map((el, index) => (
                        <Grid item key={index}>
                            <Chip
                                label={
                                    <EllipsisTooltip
                                        text={type === METADATA_FIELDS_TYPE.TAGS ? `# ${el}` : getLabelValue(el)}
                                        maxWidth={200}
                                        variant={variant ?? "body3"}
                                        // 태그 값의 연속 공백이 접혀 보이면 값이 변형된 것처럼 읽힌다 — 원문 그대로 표시한다
                                        typographyProps={
                                            type === METADATA_FIELDS_TYPE.TAGS
                                                ? { sx: { whiteSpace: "pre" } }
                                                : undefined
                                        }
                                    />
                                }
                                size="small"
                                variant="outlined"
                                disableHover={true}
                            />
                        </Grid>
                    ))}
                </Grid>
            );
        },
        [options, variant],
    );

    if (isHidden)
        return (
            <Typography variant={variant ?? "h2"} sx={{ letterSpacing: "-5px" }}>
                &middot;&middot;&middot;&middot;&middot;&middot;&middot;&middot;&middot;&middot;
            </Typography>
        );
    if ([METADATA_FIELDS_TYPE.TAGS, METADATA_FIELDS_TYPE.MULTI_SELECT].includes(type)) return getChips(type, value);
    if (type === METADATA_FIELDS_TYPE.CATEGORIES)
        return getChips(
            type,
            value.categories?.map((el) => el.name),
        );
    if (type === METADATA_FIELDS_TYPE.ATTRIBUTIONS)
        return (
            <Grid container flexDirection={"column"}>
                <Grid item>
                    <List disablePadding>
                        {!value?.length && "-"}
                        {value?.map((el, index) => (
                            <ListItem key={index} disablePadding sx={{ mb: 1, "&:last-of-type": { mb: 0 } }}>
                                <Grid container flexDirection={"row"} alignItems="center">
                                    {/* attribution 은 앞뒤 공백을 허용해 원문 그대로 저장된다 — 공백이 접히지 않게 그대로 노출한다 */}
                                    <Grid item pr={0.5}>
                                        <Typography
                                            variant={variant ?? "body3_700"}
                                            component={"p"}
                                            sx={{ whiteSpace: "pre-wrap" }}
                                        >
                                            {`${el.key} : `}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant={variant ?? "body3_400"}
                                            component={"p"}
                                            sx={{ whiteSpace: "pre-wrap" }}
                                        >
                                            {el.value}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </ListItem>
                        ))}
                    </List>
                </Grid>
            </Grid>
        );
    if (type === METADATA_FIELDS_TYPE.PEOPLE)
        return value?.length ? (
            <AutocompleteTableList
                type={"CONTENT"}
                data={[{ value }]}
                openNewWindow={true}
                onClickName={(id) => menuService.paths.contentsPeopleDetail(spaceId, projectId, id)}
                columns={PeopleContentColumn.filter((el) => el.id !== "desiredStatus")}
                gridStyle={{ border: "none" }}
            />
        ) : (
            <Typography variant={variant ?? "body3_400"} component={"p"}>
                -
            </Typography>
        );

    if (value === "") return "-"; //Note: 여기부터 아래는 문자열로만 출력되는 값들인데, value===""인 경우 ??로 걸러지지 않아 "-" 표시 출력을 위해 사용하였습니다.
    if ([METADATA_FIELDS_TYPE.TEXT_FIELD, METADATA_FIELDS_TYPE.TEXT_AREA, METADATA_FIELDS_TYPE.NUMBER].includes(type))
        return (
            <Typography variant={variant} sx={{ whiteSpace: type === METADATA_FIELDS_TYPE.TEXT_AREA ? "pre-wrap" : "normal" }}>
                {/* 자유 입력 텍스트(설명·커스텀 메타데이터 string)에 섞인 주소는 링크로 열 수 있게 한다 — 숫자는 대상이 아니다 */}
                {type === METADATA_FIELDS_TYPE.NUMBER ? (value ?? "-") : <LinkText text={value ?? "-"} />}
            </Typography>
        );
    if (type === METADATA_FIELDS_TYPE.SINGLE_SELECT) {
        const checkOptionLabel = !!options?.find((option) => !!option.label);

        return (
            <Typography variant={variant}>
                {checkOptionLabel ? options?.find((option) => option.value === value)?.label : value}
            </Typography>
        );
    }
    if (type === METADATA_FIELDS_TYPE.SINGLE_DATE_PICKER) return <Typography variant={variant}>{value ?? "-"}</Typography>;
    if (type === METADATA_FIELDS_TYPE.RANGE_DATE_PICKER) return <Typography variant={variant}>{value ?? "-"}</Typography>;

    return <></>;
};

export default MetadataFieldReadMode;
